import { v4 as uuid } from 'uuid'
import {
  TILE_CONTENT,
  isCenterpiece,
  isCollage,
} from '@mixtiles/web-backend-shared'
import {
  migrateTileContent,
  migrateHoneycombTiles,
  migrateCropParams,
  filterOldArtTiles,
} from 'components/TilesProvider/TileMigration'
import { logger } from 'services/logger'
import { type BaseTileStore } from './BaseTileStore'
import { SinglePhotoTileStore } from './SinglePhotoTileStore'
import { ArtPhotoTileStore } from './ArtPhotoTileStore'
import { AIGenPhotoTileStore } from './AIGenPhotoTileStore'
import { CollageTileStore } from './CollageTileStore'
import { CenterpieceTileStore } from './CenterpieceTileStore'
import { TileDataSchema, type TileData } from './tileSchemas'
import type { PhotoBankStore } from '../../photoBankStore/PhotoBankStore'

function migrateLegacyStyle(
  data: Record<string, unknown>
): Record<string, unknown> {
  const style = data.style as Record<string, unknown> | undefined
  const customStyle = data.customStyle as Record<string, unknown> | undefined
  if (style?.color && !customStyle?.frameColor) {
    return {
      ...data,
      customStyle: {
        ...customStyle,
        frameColor: style.color,
      },
    }
  }
  return data
}

function normalizeCollageData(
  data: Record<string, unknown>
): Record<string, unknown> {
  if (data.collage) return data

  const layout = data.layout
  const photos = data.photos
  const collageText = data.collageText
  if (!layout) return data

  const { layout: _, photos: __, collageText: ___, ...rest } = data
  return {
    ...rest,
    collage: {
      layout,
      photos: photos ?? {},
      collageText: collageText ?? {
        showText: false,
        text: '',
        textStyleId: null,
      },
    },
  }
}

export function determineTileContent(
  data: Record<string, unknown>
): TILE_CONTENT {
  if (data.tileContent) return data.tileContent as TILE_CONTENT
  if (isCenterpiece(data as { tileSize: string }))
    return TILE_CONTENT.CENTERPIECE
  if (isCollage(data as { tileSize: string })) return TILE_CONTENT.COLLAGE
  if (data.artwork) return TILE_CONTENT.ART_PHOTO
  if (data.aiTemplate) return TILE_CONTENT.AI_GEN_PHOTO
  return TILE_CONTENT.PHOTO
}

export function instantiateTile(
  data: TileData,
  photoBankStore: PhotoBankStore
): BaseTileStore {
  switch (data.tileContent) {
    case TILE_CONTENT.PHOTO:
      return new SinglePhotoTileStore(data, photoBankStore)
    case TILE_CONTENT.ART_PHOTO:
      return new ArtPhotoTileStore(data, photoBankStore)
    case TILE_CONTENT.AI_GEN_PHOTO:
      return new AIGenPhotoTileStore(data, photoBankStore)
    case TILE_CONTENT.COLLAGE:
      return new CollageTileStore(data, photoBankStore)
    case TILE_CONTENT.CENTERPIECE:
      return new CenterpieceTileStore(data, photoBankStore)
  }
}

export function parseAndMigrateTileData(
  rawData: Record<string, unknown>
): TileData {
  let data: Record<string, unknown> = {
    ...rawData,
    id: (rawData.id as string) ?? uuid(),
    uploadedAt: (rawData.uploadedAt as number) ?? Date.now(),
  }

  data = migrateTileContent(data)
  data = migrateHoneycombTiles(data)
  data = migrateCropParams(data)
  data = migrateLegacyStyle(data)

  const tileContent = determineTileContent(data)

  if (tileContent === TILE_CONTENT.COLLAGE) {
    data = normalizeCollageData(data)
  }

  data.tileContent = tileContent
  return TileDataSchema.parse(data)
}

export function parseTileStoreFromTileData(
  rawData: Record<string, unknown>,
  photoBankStore: PhotoBankStore
): BaseTileStore {
  const data = parseAndMigrateTileData(rawData)
  return instantiateTile(data, photoBankStore)
}

export function parseTileStoresFromRawTiles(
  rawTiles: Record<string, unknown>[],
  photoBankStore: PhotoBankStore
): BaseTileStore[] {
  const tileStores: BaseTileStore[] = []

  for (const rawTile of filterOldArtTiles(rawTiles)) {
    try {
      const tileStore = parseTileStoreFromTileData(rawTile, photoBankStore)
      if (tileStore.hasValidRequiredPhotos()) {
        tileStores.push(tileStore)
      }
    } catch (error) {
      logger.error('Failed to parse tile from storage, skipping tile', error, {
        rawTile,
      })
    }
  }

  return tileStores
}
